import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { usePageTitle } from "@/hooks/usePageMeta";

const values = [
  {
    icon: "ri-focus-3-line",
    title: "信息完整",
    desc: "每部作品都配有导演、主演、类型、地区、上映时间与剧情简介，减少反复跳转找信息的时间。",
  },
  {
    icon: "ri-filter-3-line",
    title: "筛选直接",
    desc: "类型、地区、年代三维度组合筛选，配合上映时间、评分与热度排序，几秒锁定目标。",
  },
  {
    icon: "ri-shield-check-line",
    title: "明确边界",
    desc: "我们清楚标注哪些是演示内容，不承诺无法交付的播放与下载能力，也不打扰你。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "合作邮箱", value: "service@ccvideo.demo" },
  { icon: "ri-customer-service-2-line", label: "客服电话", value: "400-000-0000" },
  { icon: "ri-time-line", label: "服务时间", value: "工作日 09:00 - 18:00" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "上海市黄浦区南京东路 100 号" },
];

const faqs = [
  {
    q: "CC视频可以在线播放电影吗？",
    a: "目前站内播放区域为调试演示，不提供真实视频播放与下载服务。所有影片信息与海报仅用于界面展示。",
  },
  {
    q: "收藏的内容会同步到其他设备吗？",
    a: "不会。收藏与浏览记录保存在当前浏览器的本地存储中，更换设备或清理浏览器数据后会丢失。",
  },
  {
    q: "影片评分和播放量来自哪里？",
    a: "当前为演示数据，用于展示页面结构。接入真实内容源后，将由运营后台统一维护。",
  },
  {
    q: "可以申请收录或纠正影片信息吗？",
    a: "可以。请通过合作邮箱联系我们，附上影片名称与需要修正的字段，我们会尽快核对。",
  },
];

const termsText = [
  "一、服务说明：CC视频为影视信息聚合的演示站点，提供影片资料浏览、分类检索与资讯阅读功能。本站不提供任何影视作品的在线播放、下载或点播服务。",
  "二、内容权利：站内展示的影片名称、海报、简介等素材的权利归原著作权人所有，仅用于界面演示与信息介绍。如权利人认为存在不当使用，可通过联系邮箱提出，我们将在核实后处理。",
  "三、用户行为：浏览本站时请勿进行自动化抓取、批量复制或其他影响服务稳定的行为。收藏与浏览记录仅保存在你本人的浏览器本地。",
  "四、免责声明：本站信息可能存在滞后或不准确，内容仅供参考，不构成任何形式的承诺或保证。",
];

const privacyText = [
  "一、我们收集什么：本站不要求注册登录，不收集姓名、手机号、身份证等个人身份信息。仅在浏览器本地保存你的收藏列表与浏览记录。",
  "二、本地存储：收藏（cc_favorites）与浏览记录（cc_history）通过浏览器 localStorage 保存在你的设备上，不会上传到服务器，你可以随时清理浏览器数据来删除它们。",
  "三、第三方：本站不使用第三方广告或用户画像追踪 SDK。页面引用的字体与图标资源由对应 CDN 提供，可能记录常规访问日志。",
  "四、联系方式：如对隐私处理有疑问，可发送邮件至 service@ccvideo.demo。",
];

export default function AboutPage() {
  const [modal, setModal] = useState<null | "terms" | "privacy" | "contact">(null);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  usePageTitle(
    "关于我们 - CC视频",
    "了解CC视频的定位、使命与联系方式。CC视频是面向影视爱好者的内容聚合平台，提供电影、电视剧分类浏览与影视资讯阅读。",
  );

  return (
    <SiteLayout>
      <section className="relative w-full bg-primary-950 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=wide%20cinematic%20shot%20of%20an%20empty%20modern%20cinema%20hall%20with%20deep%20navy%20walls%20and%20a%20warm%20orange%20projector%20beam%20cutting%20through%20haze%2C%20abstract%20geometric%20seating%20rows%2C%20elegant%20minimal%20editorial%20photography%2C%20high%20contrast&width=1600&height=560&seq=cc-about-hero&orientation=landscape"
          alt="CC视频品牌背景"
          title="关于CC视频 - 专业影视视频平台"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/40" />
        <div className="relative z-10 w-full px-4 md:px-6 py-14 md:py-20">
          <nav className="flex items-center gap-1.5 text-xs text-background-400 mb-5">
            <Link to="/" className="hover:text-accent-400 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
            <span className="text-accent-400">关于我们</span>
          </nav>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-label tracking-[0.18em] uppercase text-accent-400 mb-3">
            <span className="w-4 h-px bg-accent-500" />
            About CC视频
          </span>
          <h1 className="font-heading text-2xl md:text-4xl font-black text-background-50 leading-tight tracking-tight max-w-2xl">
            让「找一部好片」
            <br className="hidden md:block" />
            成为一件轻松的事
          </h1>
          <p className="mt-4 text-sm md:text-base text-background-300 leading-relaxed max-w-2xl">
            CC视频面向广大影视爱好者，聚合电影与电视剧的完整信息，涵盖影视资讯与相关内容，
            用清晰的分类与筛选，满足便捷观看信息获取的需求。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              我们的使命
            </h2>
            <p className="mt-4 text-sm md:text-[15px] text-foreground-700 leading-[1.9]">
              影视内容的丰富程度，早已超过了任何人能消化完的速度。真正的难题不是「看不到」，
              而是「不知道看什么」。CC视频希望用专业的信息组织方式，把每一部作品的要素说清楚：
              谁拍的、谁演的、什么类型、哪个年代、大致在讲什么。
            </p>
            <p className="mt-4 text-sm md:text-[15px] text-foreground-700 leading-[1.9]">
              我们不做夸张的推荐，也不制造焦虑。你只需要按自己的偏好筛选，然后决定今晚看什么。
            </p>
            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4">
              <p className="text-xs text-foreground-600 leading-relaxed">
                <strong className="text-foreground-950">平台现状说明：</strong>
                当前站点为演示版本，影片数据与资讯内容均为示范数据，播放区域为调试占位，
                不提供真实视频播放服务。
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {values.map((v, i) => (
              <div
                key={v.title}
                className={`rounded-lg border border-background-200 bg-background-50 p-5 ${
                  i === 2 ? "sm:col-span-2" : ""
                }`}
              >
                <span className="w-10 h-10 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center text-lg">
                  <i className={v.icon} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                  {v.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6">
        <div className="rounded-lg bg-primary-950 px-4 md:px-10 py-8 md:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <h2 className="font-heading text-lg md:text-xl font-bold text-background-50">
                联系我们
              </h2>
              <p className="mt-3 text-sm text-background-300 leading-relaxed">
                合作、内容纠错或使用问题，欢迎随时通过以下方式与我们取得联系。
              </p>
              <button
                type="button"
                onClick={() => setModal("contact")}
                className="mt-5 h-10 px-5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                联系客服
              </button>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {contacts.map((c) => (
                <button
                  key={c.label}
                  type="button"
                  onClick={() => setModal("contact")}
                  className="flex items-start gap-3 rounded-md border border-background-50/15 bg-background-50/5 p-4 text-left hover:border-accent-500/60 transition-colors cursor-pointer"
                >
                  <span className="w-8 h-8 rounded-md bg-background-50/10 text-accent-400 flex items-center justify-center shrink-0">
                    <i className={`${c.icon} text-base`} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[11px] text-background-400">{c.label}</span>
                    <span className="block mt-0.5 text-sm text-background-100 truncate">
                      {c.value}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              常见问题
            </h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              关于平台能力范围的一些说明，希望能先回答你大部分的疑问。
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setModal("terms")}
                id="terms"
                className="flex items-center justify-between h-11 px-4 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 hover:border-secondary-400 transition-colors cursor-pointer"
              >
                查看使用条款
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-foreground-400" />
              </button>
              <button
                type="button"
                onClick={() => setModal("privacy")}
                id="privacy"
                className="flex items-center justify-between h-11 px-4 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 hover:border-secondary-400 transition-colors cursor-pointer"
              >
                查看隐私政策
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-foreground-400" />
              </button>
            </div>
          </div>

          <div className="lg:col-span-7 divide-y divide-background-200 border-y border-background-200">
            {faqs.map((f, i) => (
              <div key={f.q}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-start justify-between gap-4 py-4 text-left cursor-pointer"
                >
                  <h3 className="font-heading text-sm md:text-[15px] font-medium text-foreground-950 leading-snug">
                    {f.q}
                  </h3>
                  <span className="w-6 h-6 flex items-center justify-center text-foreground-400 shrink-0">
                    <i className={openFaq === i ? "ri-subtract-line" : "ri-add-line"} />
                  </span>
                </button>
                {openFaq === i && (
                  <p className="pb-4 -mt-1 text-sm text-foreground-600 leading-relaxed pr-8 cc-fade-in">
                    {f.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {modal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8">
          <button
            type="button"
            aria-label="关闭弹窗"
            onClick={() => setModal(null)}
            className="absolute inset-0 bg-primary-950/60 cursor-pointer"
          />
          <div className="relative w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-lg bg-background-50 p-5 md:p-6 cc-fade-in">
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="font-heading text-base font-bold text-foreground-950">
                {modal === "terms"
                  ? "使用条款"
                  : modal === "privacy"
                    ? "隐私政策"
                    : "联系客服"}
              </h3>
              <button
                type="button"
                onClick={() => setModal(null)}
                aria-label="关闭"
                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer shrink-0"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {modal === "contact" ? (
              <div className="space-y-3">
                <p className="text-sm text-foreground-700 leading-relaxed">
                  你可以通过以下任一方式联系我们，我们会在工作日 24 小时内回复。
                </p>
                {contacts.map((c) => (
                  <div
                    key={c.label}
                    className="flex items-center justify-between gap-3 px-3.5 py-3 rounded-md bg-background-100 border border-background-200"
                  >
                    <span className="text-xs text-foreground-500">{c.label}</span>
                    <span className="text-sm text-foreground-900 text-right">{c.value}</span>
                  </div>
                ))}
                <p className="text-[11px] text-foreground-400 leading-relaxed pt-1">
                  以上联系方式为演示信息，不接入真实客服系统。
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {(modal === "terms" ? termsText : privacyText).map((t, i) => (
                  <p key={i} className="text-sm text-foreground-700 leading-relaxed">
                    {t}
                  </p>
                ))}
                <p className="text-[11px] text-foreground-400">最后更新：2026 年 10 月</p>
              </div>
            )}

            <button
              type="button"
              onClick={() => setModal(null)}
              className="mt-5 w-full h-10 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}